import { create } from 'zustand';

const SESSION_KEY = 'eximerp.chat.sessionId';

function readSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

function writeSessionId(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SESSION_KEY, id);
  } catch {
    // localStorage may be unavailable (Safari private mode, quota, etc.) — ignore.
  }
}

function generateUuid(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function ensureSessionId(): string {
  const existing = readSessionId();
  if (existing) return existing;
  const fresh = generateUuid();
  writeSessionId(fresh);
  return fresh;
}

export interface ChatMessage {
  id: string;
  [key: string]: unknown;
}

export type ChatContact = Record<string, unknown> | null;

export interface ChatWidgetState {
  isOpen: boolean;
  messages: ChatMessage[];
  handoffActive: boolean;
  sessionId: string;
  contact: ChatContact;

  open: () => void;
  close: () => void;
  toggle: () => void;
  addMessage: (message: Record<string, unknown>) => void;
  setHandoffActive: (value: unknown) => void;
  setContact: (contact: ChatContact) => void;
  reset: () => void;
}

export const useChatWidgetStore = create<ChatWidgetState>()(set => ({
  isOpen: false,
  messages: [],
  handoffActive: false,
  sessionId: ensureSessionId(),
  contact: null,

  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set(state => ({ isOpen: !state.isOpen })),

  addMessage: message =>
    set(state => ({
      messages: [...state.messages, { id: generateUuid(), ...message }],
    })),

  setHandoffActive: value => set({ handoffActive: !!value }),

  setContact: contact => set({ contact }),

  reset: () => {
    const fresh = generateUuid();
    writeSessionId(fresh);
    set({
      messages: [],
      handoffActive: false,
      sessionId: fresh,
      contact: null,
    });
  },
}));

export const __test__ = { SESSION_KEY };
